import { decodeFunctionResult, encodeFunctionData, encodeFunctionResult, getAddress, isAddress, zeroAddress,
  type Abi, type Address, type Hex } from "viem";
import { creatorFeeCurveAbi, creatorFeeEscrowAbi, creatorFeeFactoryAbi, creatorFeeReceiverAbi,
  creatorFeeVaultAbi } from "../contracts/creator-fees";
import { contractReadError } from "../contracts/contract-errors";
import { ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import { calculateRedeemFeeQuote } from "../lib/redeem-fee-quote";
import type { Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadMethod, type RpcReadOptions } from "./read-rpc";
import { observeRedeemDeployment, type RedeemDeploymentObservation } from "./redeem-fee-route";

/** Injected read context, NOT a backend DTO or a new production permission. */
export type RedeemFeeRoute = Readonly<{
  kind: string; support: "UNVERIFIED" | "ISOLATED_TEST_ONLY" | "PINNED_RPC_READ_ONLY";
  environment: "development" | "test" | "production"; deploymentRevision: string;
  chainId: number; collection: Address; sourceToken: Address; curve: Address;
  vault: Address; receiver: Address; factory: Address; escrow: Address; instanceId: Hex;
  observedDeployment?: RedeemDeploymentObservation;
}>;
export type RedeemFeeQuoteInput = {
  account: Address; tokenId: bigint; contextRevision: string; route: RedeemFeeRoute | null;
};
export type RedeemFeeQuote = Readonly<ReturnType<typeof calculateRedeemFeeQuote> & {
  owner: Address; status: 3; buybackEnabled: false;
  evidence: "ISOLATED_TEST_ONLY" | "PINNED_RPC_READ_ONLY"; executionAvailable: false;
  context: Readonly<RedeemFeeRoute & { account: Address; tokenId: bigint; contextRevision: string }>;
  provenance: Readonly<{ blockNumber: bigint; blockHash: Hex; blockTimestamp: bigint;
    consistency: "BLOCK_HASH" | "BLOCK_NUMBER_RECHECKED" }>;
}>;
export type RedeemFeeQuoteUnavailableReason = "ROUTE_UNSUPPORTED" | "INVALID_CONTEXT" | "WRONG_CHAIN" |
  "INVALID_RESPONSE" | "RPC_ERROR" | "NFT_UNAVAILABLE" | "NOT_OWNER" | "NOT_FINALIZED" |
  "BINDING_MISMATCH" | "BUYBACK_UNSUPPORTED" | "EXTRA_ESCROW_UNSUPPORTED" | "SNAPSHOT_CHANGED" | "PAYOUT_ZERO";
export type RedeemFeeQuoteResult = { available: true; quote: RedeemFeeQuote } |
  { available: false; reason: RedeemFeeQuoteUnavailableReason; message: string };
export type RedeemFeeQuoteReadOptions = RpcReadOptions & {
  /** Explicit numeric pin for RPCs lacking EIP-1898; never fall back to latest. */
  blockReference?: "hash" | "number";
};

class Unavailable extends Error {
  constructor(readonly reason: RedeemFeeQuoteUnavailableReason, message: string) { super(message); }
}
function unavailable(reason: RedeemFeeQuoteUnavailableReason, message: string): never { throw new Unavailable(reason, message); }
function address(value: unknown, allowZero = false): Address {
  if (typeof value !== "string" || !isAddress(value, { strict: false }) || !allowZero && value.toLowerCase() === zeroAddress) {
    return unavailable("INVALID_RESPONSE", "Invalid contract address.");
  }
  return getAddress(value);
}
function matches(value: unknown, expected: Address): void {
  if (address(value, expected === zeroAddress).toLowerCase() !== expected.toLowerCase()) {
    unavailable("BINDING_MISMATCH", "Source, curve, vault or receiver binding mismatch.");
  }
}
function uint(value: unknown): bigint {
  if (typeof value !== "bigint" || value < 0n || value >= (1n << 256n)) return unavailable("INVALID_RESPONSE", "Invalid uint256 response.");
  return value;
}
function boundedNumber(value: unknown, maximum: number): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > maximum) {
    return unavailable("INVALID_RESPONSE", "Invalid NFT status or reward rate.");
  }
  return value;
}
function blockHeader(value: unknown) {
  if (!value || typeof value !== "object") return unavailable("INVALID_RESPONSE", "Missing quote block.");
  const header = value as Record<string, unknown>;
  if (typeof header.number !== "string" || !/^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(header.number) ||
    typeof header.timestamp !== "string" || !/^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(header.timestamp) ||
    typeof header.hash !== "string" || !/^0x[0-9a-f]{64}$/i.test(header.hash) || BigInt(header.hash) === 0n) {
    return unavailable("INVALID_RESPONSE", "Invalid quote block identity.");
  }
  return { number: header.number as Hex, blockNumber: uint(BigInt(header.number)), blockHash: header.hash as Hex,
    blockTimestamp: uint(BigInt(header.timestamp)) };
}

/** Observation only. Abort/revision failures propagate; other failures stay unavailable.
 * Runtime discovery admits pinned read-only observations; neither kind grants execution.
 * The caller supplies its existing WalletOperation.assertCurrent or generation guard.
 */
export async function readRedeemFeeQuote(provider: Eip1193Provider, input: RedeemFeeQuoteInput,
  assertCurrent: () => void, options: RedeemFeeQuoteReadOptions = {}): Promise<RedeemFeeQuoteResult> {
  const guard = () => { options.signal?.throwIfAborted(); assertCurrent(); };
  guard();
  const route = input.route ? { ...input.route } : null;
  const { account, tokenId, contextRevision } = input;
  try {
    if (!route || route.kind !== "REDEEM_WITH_FEES_CURVE_V1" ||
      !(route.support === "ISOLATED_TEST_ONLY" && ["test", "development"].includes(route.environment) ||
        route.support === "PINNED_RPC_READ_ONLY" && route.observedDeployment &&
        ["test", "development", "production"].includes(route.environment))) {
      unavailable("ROUTE_UNSUPPORTED", "A pinned read-only route or explicitly isolated quote context is required.");
    }
    if (!Number.isSafeInteger(route.chainId) || route.chainId <= 0 ||
      typeof route.deploymentRevision !== "string" || !route.deploymentRevision.trim() ||
      typeof contextRevision !== "string" || !contextRevision.trim() ||
      typeof tokenId !== "bigint" || tokenId < 0n || tokenId >= (1n << 256n) ||
      !/^0x[0-9a-f]{64}$/i.test(route.instanceId) || BigInt(route.instanceId) === 0n ||
      typeof account !== "string" || !isAddress(account, { strict: false }) || account.toLowerCase() === zeroAddress ||
      [route.collection, route.sourceToken, route.curve, route.vault, route.receiver, route.factory, route.escrow]
        .some(value => typeof value !== "string" || !isAddress(value, { strict: false }) || value.toLowerCase() === zeroAddress)) {
      unavailable("INVALID_CONTEXT", "A complete validated deployment and wallet context is required.");
    }
    const request = async (method: RpcReadMethod, params: readonly unknown[]) => {
      guard();
      try {
        const raw = await readRpc(provider, { method, params }, options);
        guard();
        return raw;
      } catch (error) {
        guard();
        if (error instanceof Unavailable) throw error;
        const decoded = contractReadError(error);
        unavailable(decoded.message.startsWith("ERC721NonexistentToken:") ? "NFT_UNAVAILABLE" : "RPC_ERROR", decoded.message);
      }
    };
    const checkChain = async () => {
      const chain = await request("eth_chainId", []);
      if (typeof chain !== "string" || !/^0x[0-9a-f]+$/i.test(chain)) unavailable("INVALID_RESPONSE", "Invalid chain response.");
      if (BigInt(chain) !== BigInt(route.chainId)) unavailable("WRONG_CHAIN", "Select the execution network before quoting redemption.");
    };
    await checkChain(); // No contract address is queried on a known wrong network.
    const pin = blockHeader(await request("eth_getBlockByNumber", ["latest", false]));
    const reference = options.blockReference === "number" ? pin.number : { blockHash: pin.blockHash, requireCanonical: true } as const;
    if (route.observedDeployment) {
      const observed = await observeRedeemDeployment(provider, route.collection, reference, guard, options).catch(error => {
        guard();
        return unavailable("RPC_ERROR", contractReadError(error).message);
      });
      const expected = route.observedDeployment;
      if (observed.collectionCodeHash !== expected.collectionCodeHash ||
        observed.implementation?.toLowerCase() !== expected.implementation?.toLowerCase() ||
        observed.implementationCodeHash !== expected.implementationCodeHash) {
        unavailable("BINDING_MISMATCH", "Redemption implementation changed. Resolve the route and review again.");
      }
    }
    const read = async (to: Address, abi: Abi, functionName: string, args: readonly unknown[] = []) => {
      const raw = await request("eth_call", [{ to, data: encodeFunctionData({ abi, functionName, args }) }, reference]);
      if (typeof raw !== "string" || !/^0x(?:[0-9a-f]{2})+$/i.test(raw)) unavailable("INVALID_RESPONSE", "Invalid contract read data.");
      try {
        const result = decodeFunctionResult({ abi, functionName, data: raw as Hex });
        // Reject trailing bytes and noncanonical bool/address/narrow integer words.
        if (encodeFunctionResult({ abi, functionName, result }).toLowerCase() !== raw.toLowerCase()) throw new Error("Noncanonical response");
        return result;
      } catch { return unavailable("INVALID_RESPONSE", "Malformed contract response."); }
    };
    const codeChecks = [route.collection, route.curve, route.vault, route.receiver, route.factory, route.escrow].map(async to => {
      const code = await request("eth_getCode", [to, reference]);
      if (typeof code !== "string" || !/^0x(?:[0-9a-f]{2})+$/i.test(code)) unavailable("BINDING_MISMATCH", "A configured route target has no valid contract code.");
    });
    const [ownerValue, statusValue, stateValue, vaultValue, instanceValue] = await Promise.all([
      read(route.collection, ponsRewardNftAbi, "ownerOf", [tokenId]),
      read(route.collection, ponsRewardNftAbi, "rewardStatus", [tokenId]),
      read(route.collection, ponsRewardNftAbi, "nftState", [tokenId]),
      read(route.collection, ponsRewardNftAbi, "FEE_VAULT"),
      read(route.collection, ponsRewardNftAbi, "INSTANCE_ID"),
      ...codeChecks,
    ]);
    guard();
    const owner = address(ownerValue);
    if (owner.toLowerCase() !== account.toLowerCase()) unavailable("NOT_OWNER", "Only the current NFT owner can quote redemption.");
    if (!Array.isArray(stateValue) || stateValue.length !== 10) unavailable("INVALID_RESPONSE", "Invalid NFT state.");
    const status = boundedNumber(statusValue, 4), stateStatus = boundedNumber(stateValue[4], 4);
    if (status !== stateStatus) unavailable("INVALID_RESPONSE", "NFT status reads disagree.");
    if (status !== 3) unavailable("NOT_FINALIZED", "Only a finalized NFT can redeem.");
    if (uint(stateValue[5]) !== 1n) unavailable("INVALID_RESPONSE", "The legacy sweep route requires reward policy V1.");
    const rewardBps = boundedNumber(stateValue[2], 10_000);
    if (rewardBps === 0) unavailable("INVALID_RESPONSE", "Finalized reward rate must be positive.");
    boundedNumber(stateValue[3], 4);
    matches(stateValue[0], route.sourceToken);
    matches(vaultValue, route.vault);
    if (typeof instanceValue !== "string" || instanceValue.toLowerCase() !== route.instanceId.toLowerCase()) {
      unavailable("BINDING_MISMATCH", "Collection instance mismatch.");
    }
    await checkChain();
    const [curveToken, curveFactory, pair, recipient, curveEscrow, graduated, buyback, launchValue, controller,
      vaultReceiver, receiverToken, receiverVault, receiverEscrow, receiverInstance] = await Promise.all([
      read(route.curve, creatorFeeCurveAbi, "token"), read(route.curve, creatorFeeCurveAbi, "factory"),
      read(route.curve, creatorFeeCurveAbi, "pairToken"), read(route.curve, creatorFeeCurveAbi, "deployer"),
      read(route.curve, creatorFeeCurveAbi, "feeEscrow"), read(route.curve, creatorFeeCurveAbi, "graduated"),
      read(route.curve, creatorFeeCurveAbi, "buybackEnabled"),
      read(route.factory, creatorFeeFactoryAbi, "getLaunchedToken", [route.sourceToken]),
      read(route.vault, creatorFeeVaultAbi, "redemptionController"),
      read(route.vault, creatorFeeVaultAbi, "sourceFeeReceiver", [route.sourceToken]),
      read(route.receiver, creatorFeeReceiverAbi, "SOURCE_TOKEN"), read(route.receiver, creatorFeeReceiverAbi, "FEE_VAULT"),
      read(route.receiver, creatorFeeReceiverAbi, "UPSTREAM_ESCROW"), read(route.receiver, creatorFeeReceiverAbi, "INSTANCE_ID"),
    ]);
    guard();
    matches(curveToken, route.sourceToken); matches(curveFactory, route.factory); matches(pair, zeroAddress);
    matches(recipient, route.receiver); matches(curveEscrow, route.escrow); matches(controller, route.collection);
    matches(vaultReceiver, route.receiver); matches(receiverToken, route.sourceToken);
    matches(receiverVault, route.vault); matches(receiverEscrow, route.escrow);
    if (typeof receiverInstance !== "string" || receiverInstance.toLowerCase() !== route.instanceId.toLowerCase()) {
      unavailable("BINDING_MISMATCH", "Receiver instance mismatch.");
    }
    if (graduated !== false) unavailable("ROUTE_UNSUPPORTED", "Only the native pre-graduation curve route is supported.");
    if (buyback !== false) unavailable("BUYBACK_UNSUPPORTED", "The sweep formula requires buyback to be off.");
    if (!launchValue || typeof launchValue !== "object" || Array.isArray(launchValue)) unavailable("INVALID_RESPONSE", "Invalid launch binding.");
    const launch = launchValue as Record<string, unknown>;
    matches(launch.token, route.sourceToken); matches(launch.curve, route.curve);
    matches(launch.creatorFeeRecipient, route.receiver); matches(launch.pairToken, zeroAddress);
    if (launch.exists !== true || launch.phase !== 0) unavailable("ROUTE_UNSUPPORTED", "No active pre-graduation source binding.");
    if (launch.buybackEnabled !== false) unavailable("BUYBACK_UNSUPPORTED", "Factory buyback must be off.");
    await checkChain();
    const [pool, fee, tax, share, escrowBalance] = await Promise.all([
      read(route.vault, creatorFeeVaultAbi, "availableRewardWei", [route.sourceToken]),
      read(route.curve, creatorFeeCurveAbi, "quoteFeeBalance"), read(route.curve, creatorFeeCurveAbi, "creatorTaxBalance"),
      read(route.curve, creatorFeeCurveAbi, "protocolFeeShareBps"),
      read(route.escrow, creatorFeeEscrowAbi, "balanceOf", [route.receiver]),
    ]);
    guard();
    if (uint(escrowBalance) !== 0n) unavailable("EXTRA_ESCROW_UNSUPPORTED", "Pre-existing receiver escrow is outside the specified sweep quote.");
    let amounts: ReturnType<typeof calculateRedeemFeeQuote>;
    try { amounts = calculateRedeemFeeQuote({ vaultPoolWei: uint(pool), quoteFeeWei: uint(fee), creatorTaxWei: uint(tax),
      protocolShareBps: uint(share), rewardBps: BigInt(rewardBps), buybackEnabled: false }); }
    catch (error) {
      if (error instanceof Unavailable) throw error;
      unavailable("INVALID_RESPONSE", "Invalid fee amounts, protocol share or post-sweep pool.");
    }
    if (amounts.payoutWei === 0n) unavailable("PAYOUT_ZERO", "The estimated sweep payout rounds to zero.");
    const recheck = blockHeader(await request("eth_getBlockByNumber", [pin.number, false]));
    if (recheck.blockNumber !== pin.blockNumber || recheck.blockHash.toLowerCase() !== pin.blockHash.toLowerCase() ||
      recheck.blockTimestamp !== pin.blockTimestamp) unavailable("SNAPSHOT_CHANGED", "The quote block changed. Obtain a new quote.");
    await checkChain();
    guard();
    return { available: true, quote: Object.freeze({ ...amounts, owner, status: 3, buybackEnabled: false,
      evidence: route.support === "PINNED_RPC_READ_ONLY" ? "PINNED_RPC_READ_ONLY" : "ISOLATED_TEST_ONLY", executionAvailable: false,
      context: Object.freeze({ ...route, account: getAddress(account), tokenId, contextRevision }),
      provenance: Object.freeze({ blockNumber: pin.blockNumber, blockHash: pin.blockHash, blockTimestamp: pin.blockTimestamp,
        consistency: options.blockReference === "number" ? "BLOCK_NUMBER_RECHECKED" : "BLOCK_HASH" }) }) };
  } catch (error) {
    guard();
    if (error instanceof Unavailable) return { available: false, reason: error.reason, message: error.message };
    // Unexpected programming errors and revision changes are not an empty quote.
    throw error;
  }
}
